import Logger from "../Logger";
import Device from "../models/Device";
import { showToast } from "../components/Toasts";
import { useEffect, useRef, useState } from "react";
import { channelTypeInfo } from "../models/ChannelType";
import KnxBridge from "../KnxBridge";
import { AssociationListEntry } from "../models/programming/DeviceProgramming";

const logger = new Logger();

// ETS Download Modes:
export enum downloadMode {
  parameters = 0,
  partialDownload = 1,
  downloadIndividualAddress = 2,
  overwriteIndividualAddress = 3,
  downloadApplication = 4,
  parametersVerifyOnly = 5, // No download; Verifies MemoryMap with projectData
  parametersDownloadAndVerify = 6,
  none = 7,
}

export default function DeviceProgramminCard(props: {
  device: Device;
  unknownDeviceInProg: string | undefined;
  loaded: boolean;
  onCardUpdate: (isUnknownDeviceProgrammed: boolean) => void;
}) {
  const { device, unknownDeviceInProg, loaded, onCardUpdate } = props;

  let progMode = false;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isSupply = channelTypeInfo(device.channelType).isSupply;

  const [isOpen, setIsOpen] = useState(false); // UI only: Three-Dot right click contextMenu
  const [programMode, setProgramMode] = useState<boolean | null>(null); // Programming mode ON/OFF
  const [addressExist, setAddressExist] = useState<boolean | null>(null); // Physical Address exist in Establishment (may not be verified device)
  const [verifiedDevice, setVerifiedDevice] = useState<boolean | null>(null); // Valid Device in Establishment with matching metadata

  const [unloadInProgress, setUnloadInProgress] = useState<boolean>(false); // True while unloading
  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false); // True while downloading

  const [bridgeConnected, setBridgeConnected] = useState<boolean>(false);

  // Click events
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false); // Close the dropdown if the click is outside
    }
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function resetDeviceStatus() {
    setProgramMode(false);
    setAddressExist(false);
    setVerifiedDevice(false);
  }

  async function setDeviceStatus() {
    setAddressExist(true);
    await verifyDevice(device);
  }

  // Verify device metadata and physical address
  useEffect(() => {
    const checkDevice = async () => {
      try {
        if (programMode == null) {
          const res = await KnxBridge.isDevicePresentInNetwork(device.fullPhysicalAddress);
          if (res == "success") {
            await setDeviceStatus();
          } else if (res == "Network Error") {
            // showToast({ text: state.translate("knxBridge.disconnectedFromNw", "Not connected to gateway.") });
            resetDeviceStatus();
          } else if (res.toLowerCase().includes("not found") || res == "") {
            resetDeviceStatus();
          }
        }
      } catch (ex) {
        console.error(ex);
        resetDeviceStatus();
      }
    };
    if (loaded) {
      checkDevice();
    }
  }, [loaded, device.fullPhysicalAddress]);

  // Polling Device status
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const pollProgramMode = async () => {
      if (verifiedDevice) {
        try {
          // const res1 = await KnxBridge.getDevicesInProgramMode();
          // onCardUpdate(true);
          const status = await KnxBridge.isDeviceInProgramMode(device.fullPhysicalAddress);
          if (status == "Service Unavailable") setBridgeConnected(false);
          else setBridgeConnected(true);

          const newProgStatus = status === "success";

          setProgramMode(newProgStatus);
          if (progMode != newProgStatus) {
            progMode = newProgStatus;
            onCardUpdate(true);
          }
        } catch (error) {
          console.error("Error in polling program mode:", error);
        }
      }
    };

    if (verifiedDevice) {
      pollProgramMode();
      intervalId = setInterval(() => {
        pollProgramMode();
      }, 5000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [verifiedDevice]);

  useEffect(() => {
    console.log("unknownDeviceInProg changed to:", unknownDeviceInProg);
  }, [unknownDeviceInProg]);

  useEffect(() => {}, [programMode, verifiedDevice, addressExist, unloadInProgress, downloadInProgress]);

  async function downloadDevice(device: Device, mode: any, newPhysicalAddress?: string) {
    try {
      if (device) {
        setDownloadInProgress(true);

        await device.updateAllFromKnxMetadataImmediately();
        // await device.template?.knxProductData.loadXmlData();
        const signalAssociationList = new Array<AssociationListEntry>();
        // Push all comObj-GrpAddr associations as well as non-associations into list,
        // both to be used for memory map
        device.allKnxSignals().forEach((signal) => {
          const a = (signal as any)?.a;
          const knxSignal = signal.knxSignal;
          // const a = (signal.knxSignal as any)?.a;
          if (a) {
            if (knxSignal && knxSignal.groupAddresses.items.length > 0) {
              // Include signals with associated GroupAddresses
              knxSignal.groupAddresses.forEach((groupAddress) => {
                signalAssociationList.push({
                  addr: groupAddress.fullAddress,
                  cob: parseInt(signal.number),
                  a: a,
                });
              });
            } else {
              // Also include signals with no associations as needed in forming ComObjTable
              signalAssociationList.push({
                addr: "",
                cob: parseInt(signal.number),
                a: a,
              });
            }
          } else {
            logger.error("missing a in signal");
          }
        });

        showToast({
          text: state.translate("knxBridge.downloadingDevice", "Downloading Device..."),
          keepVisible: true,
        });
        if (mode == downloadMode.parameters) {
          await KnxBridge.downloadDevice(device, signalAssociationList, false);
        } else if (mode == downloadMode.downloadApplication) {
          await KnxBridge.downloadDevice(device, signalAssociationList, true);
        } else if (mode == downloadMode.downloadIndividualAddress) {
          // const devices = state.allDevices().filter((dev) => dev.fullPhysicalAddress == unknownDeviceInProg);
          // if (devices.length > 1) {
          //   showToast({
          //     text: `More than one device with address ${unknownDeviceInProg} exists in Device List.`,
          //   });

          // }
          // Overwrites Address the first time a new device is downloaded
          await KnxBridge.downloadDeviceIndividualAddress(device, false, "");
          await verifyDevice(device);
        } else if (mode == downloadMode.overwriteIndividualAddress) {
          // Only for ProductProcedure: In addition to dowload IA, also modify device Address in Address Table
          // In MergedProcedure, no need as Physical Address is not stored in Table
          await KnxBridge.downloadDeviceIndividualAddress(
            device,
            true,
            newPhysicalAddress || device.fullPhysicalAddress,
          );
        } else if (mode == downloadMode.partialDownload) {
          // TODO: Does the same as "All" except for address broadcasting and assigning new address.
        }
        setDownloadInProgress(false);
        onCardUpdate(true);
        showToast({ text: state.translate("knxBridge.downloadComplete", "Download Complete.") });
      }
    } catch (ex) {
      logger.error(ex);
      setDownloadInProgress(false);
      showToast({ text: state.translate("knxBridge.downloadFailed", "Download Failed.") });
    }
  }

  async function verifyDevice(device: Device, mode?: any) {
    try {
      const status = await KnxBridge.isDeviceInProgramMode(device.fullPhysicalAddress);
      mode = mode ? mode : status == "success"; // PID 54
      setProgramMode(mode);

      const productData = device.template?.knxProductData;
      if (productData) {
        const LoadProcedureStyle = productData.productInfo.LoadProcedureStyle;

        // Verify Mask Version
        const maskFromAP = productData.productInfo.MaskVersion.split("-")[1];
        const maskFromMem = await KnxBridge.getDeviceProperty(device.fullPhysicalAddress, "83", "0"); // PID 83
        let validMask = false;

        // Some devices like BuschJeager have no mask version as an accesible property
        // So for such devices we assume by default that it is valid.
        if (maskFromMem == "") validMask = true;
        else validMask = maskFromAP.toLowerCase() == maskFromMem.toLowerCase();

        // Verify Manufacturer Id

        // const manIdFromMem = await KnxBridge.getDeviceProperty(device.fullPhysicalAddress, "12", "0"); // PID 12
        // const validManufacturer = manIdFromAP == manIdFromMem;
        // // Verify ApplicationNumber / HardwareType
        // const APNumberFromMem = await KnxBridge.getDeviceProperty(device.fullPhysicalAddress, "78", "0"); // PID 78
        // const APNumberFromMemInt = parseInt(APNumberFromMem, 16);
        // const validAPNumber = APNumberFromAPInt == APNumberFromMemInt;

        // // Verify Version (Only for MergedProcedure; PID unavailable for ProductProcedure)
        // let validVersion = false;
        // if (LoadProcedureStyle == "MergedProcedure") {
        //   const versionFromAP = productData.productInfo.MinEtsVersion;
        //   const versionFromMem = await KnxBridge.getDeviceProperty(device.fullPhysicalAddress, "25", "0"); // PID 25
        //   const APNumberFromMemFloatString = KnxBridge.hexToIEEE754(versionFromMem);
        //   validVersion = versionFromAP.toLowerCase() == APNumberFromMemFloatString.toLowerCase();
        // } else {
        //   validVersion = true;
        // }

        // Application Version (Has 3 information:  Manufacturer, Application Number and Vesion)
        // E.g. 0083 013b 32 : Manufacturer MDT (0083), App Number 0x013b = 315, Version 0x32 = 50 = 3.2
        let validAppVersion = false;
        let appVersionFromMem: string = "";

        const manIdFromAP = productData.productInfo.ManufacturerId.split("-")[1];
        const APNumberFromAP = productData.productInfo.ApplicationNumber;
        const APNumberFromAPInt = parseInt(APNumberFromAP);

        if (LoadProcedureStyle == "ProductProcedure") {
          appVersionFromMem = await KnxBridge.getDeviceProperty(device.fullPhysicalAddress, "13", "3"); // ObjType 3, PID 13
        } else if (LoadProcedureStyle == "MergedProcedure") {
          appVersionFromMem = await KnxBridge.getDeviceProperty(device.fullPhysicalAddress, "13", "4"); // ObjType 4, PID 25
        }

        if (appVersionFromMem.length == 10) {
          const manufacturer = appVersionFromMem.substring(0, 4);
          const appNumber = appVersionFromMem.substring(4, 8);
          const version = appVersionFromMem.substring(8, 10);
          if (
            manufacturer == manIdFromAP &&
            APNumberFromAPInt == parseInt(appNumber, 16) &&
            parseInt(productData.productInfo.ApplicationVersion) == parseInt(version, 16)
          ) {
            validAppVersion = true;
          }
        }

        // TODO
        // const orderIdFromMem = await KnxBridge.getDeviceProperty(device.fullPhysicalAddress, "15", "0"); // ObjType 0, PID 15

        if (validMask && validAppVersion) {
          setAddressExist(true);
          setVerifiedDevice(true);
        } else {
          setVerifiedDevice(false);
        }
      }
    } catch (error) {
      console.error("Error fetching program mode:", error);
    }
  }

  async function unloadDevice() {
    toggleMenu();
    setUnloadInProgress(true);
    // setVerifiedDevice(false);
    showToast({
      text: state.translate("knxBridge.unloadingDevice", "Unloading Device..."),
      keepVisible: true,
    });
    const status = await KnxBridge.unloadDevice(device);
    setUnloadInProgress(false);
    if (status) {
      resetDeviceStatus();
      onCardUpdate(true);
      showToast({ text: state.translate("knxBridge.unloadComplete", "Unload Complete.") });
    } else {
      showToast({ text: state.translate("knxBridge.unloadFailed", "Unload Failed.") });
    }
  }

  return (
    <div className="appItemCard" key={device.id}>
      <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <div className="appTextContainer" style={{ justifyContent: "space-between", width: "100%" }}>
            <div className="appText">{`${device.fullTag} ${device.name}`}</div>
            <div className="cardSuffix">
              {verifiedDevice && programMode && (
                <span
                  className="statusDot programActive"
                  title={state.translate("knxBridge.programModeOn", "ProgramMode On")}
                  style={{ marginLeft: "10px" }}
                ></span>
              )}
              {verifiedDevice && !programMode && (
                <span
                  className="statusDot programInactive"
                  title={state.translate("knxBridge.programModeOff", "ProgramMode Off")}
                  style={{ marginLeft: "10px" }}
                ></span>
              )}
            </div>

            {/* {addressExist && verifiedDevice == false && (
              <div className="cardSuffix">
                <div
                  className="headerHelp dialogHeaderButton"
                  data-tooltip-id="tv-tooltip"
                  data-tooltip-content={`Address found in network, but with different device metadata.`}
                  data-tooltip-place="bottom"
                >
                  <i className="las la-info"></i>
                </div>
              </div>
            )} */}
          </div>

          <div style={{ display: "flex", color: "black", fontSize: "0.5em", alignItems: "center" }}>
            <div>
              {device.fullPhysicalAddress}
              {/* {verifiedDevice == null && (
                <span>
                  {" "}
                  &nbsp;
                  <i className={`las la-spinner la-spin`}></i>
                </span>
              )} */}
              &nbsp;&nbsp;
              {verifiedDevice && (
                <i
                  className={`las la-check`}
                  data-tooltip-id="tv-tooltip"
                  data-tooltip-content={`Available`}
                  data-tooltip-place="right"
                  style={{
                    color: "green",
                    padding: "1px",
                    fontSize: "1.2em",
                    border: "1px solid green",
                    borderRadius: "20px",
                    boxShadow: "0 0 0.25cm rgba(0, 0, 0, 0.5)",
                  }}
                ></i>
              )}
              {addressExist == false && (
                <i
                  className={`las la-unlink`}
                  data-tooltip-id="tv-tooltip"
                  data-tooltip-content={`Not in network`}
                  data-tooltip-place="right"
                  style={{
                    padding: "1px",
                    fontSize: "1.2em",
                    border: "1px solid grey",
                    borderRadius: "20px",
                    boxShadow: "0 0 0.25cm rgba(0, 0, 0, 0.5)",
                  }}
                ></i>
              )}
              {addressExist && verifiedDevice == false && (
                <img
                  src="images/alert.png"
                  data-tooltip-id="tv-tooltip"
                  data-tooltip-content={`Different device type detected!`}
                  data-tooltip-place="right"
                  style={{
                    width: "25px",
                    height: "25px",
                    marginLeft: "-3px",
                    // marginRight: "5px",
                    // borderRadius: "20px",
                    // boxShadow: "0 0 0.25cm rgba(0, 0, 0, 0.5)",
                  }}
                />
              )}
            </div>
            <div className="cardSuffix" style={{ width: "30%", marginLeft: "auto", marginRight: "-15px" }}>
              {(programMode == null || verifiedDevice == null || unloadInProgress) && (
                <span className="appTextSuffix rightAlign">
                  <i className={`las la-spinner la-spin`}></i>
                </span>
              )}
              {verifiedDevice &&
                !programMode &&
                !downloadInProgress &&
                !unloadInProgress &&
                (unknownDeviceInProg == undefined || unknownDeviceInProg == "") && (
                  <button
                    className="appTextSuffix button"
                    title={state.translate("knxBridge.downloadAllParameters", "Download All Parameters")}
                    onClick={async () => {
                      showToast({
                        text: state.translate("knxBridge.downloadingDevice", "Downloading Device..."),
                        keepVisible: true,
                      });
                      await downloadDevice(device, downloadMode.parameters);
                      setProgramMode(false);
                    }}
                    // disabled={!bridgeConnected}
                  >
                    {state.translate("knxBridge.parameters", "Parameters")}{" "}
                    <i
                      className={`las la-download`}
                      style={{ marginTop: "2px", marginBottom: "-2px", fontSize: "1.2em" }}
                    ></i>
                  </button>
                )}
              {programMode == false &&
                verifiedDevice == false &&
                !downloadInProgress &&
                !unloadInProgress &&
                unknownDeviceInProg != undefined &&
                unknownDeviceInProg != "" && (
                  <button
                    className="appTextSuffix button"
                    title={state.translate("knxBridge.downloadIndividualAddress", "Download Individual Address")}
                    onClick={async () => {
                      await downloadDevice(device, downloadMode.downloadIndividualAddress);
                    }}
                    // disabled={!bridgeConnected}
                  >
                    {state.translate("knxBridge.address", "Address")} <i className={`${"las la-download"}`}></i>
                  </button>
                )}
              {downloadInProgress && (
                <button
                  className="appTextSuffix button"
                  title={state.translate("knxBridge.downloadParameters", "Download Parameters")}
                  onClick={async () => {
                    showToast({
                      text: state.translate("knxBridge.downloadingDevice", "Downloading Device..."),
                      keepVisible: true,
                    });
                    await downloadDevice(device, downloadMode.parameters);
                    setProgramMode(false);
                  }}
                  disabled={true}
                >
                  {verifiedDevice
                    ? state.translate("knxBridge.parameters", "Parameters")
                    : state.translate("knxBridge.address", "Address")}{" "}
                  <i
                    title={state.translate("knxBridge.downloading", "Downloading...")}
                    className={`las la-download falling-icon`}
                  ></i>
                </button>
              )}
              {verifiedDevice && programMode && !downloadInProgress && !unloadInProgress && (
                <button
                  className="appTextSuffix button"
                  title={state.translate("knxBridge.unloadApplicationAndAddress", "Unload Application and Address")}
                  onClick={unloadDevice}
                  // disabled={!bridgeConnected}
                >
                  {state.translate("knxBridge.unload", "Unload")} <i className={`${"las la-upload"}`}></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
